import {
	Alert,
	Box,
	Button,
	Icons,
	Inline,
	Stack,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import {
	FileDropAreaField,
	FileInputField,
	Form,
	SubmissionError,
	TextAreaInputField,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useHotkey } from "@sembark-travel/ui-hotkey"
import { useEffect, useId, useRef, useState } from "react"
import type { TWhatsAppMessage } from "../store"
import { WhatsAppQuotedMessage } from "./QuotedMessage"
import { CUSTOMER_SERVICE_WINDOW_HOURS } from "./utils"

type TComposerValues = {
	custom_message?: string
	attachment?: File | null
}

// The file types WhatsApp accepts as an attachment (image / video / audio /
// document / sticker). Shared by every composer. Meta plays only mp4 and 3gp
// as a video, and sends a WebP only as a 512×512 sticker.
const ATTACHMENT_ACCEPT =
	"image/jpeg,image/png,image/webp,video/mp4,video/3gpp,audio/aac,audio/mp4,audio/mpeg,audio/amr,audio/ogg,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain"

/**
 * The shared WhatsApp reply box: a text area + optional attachment field + Send
 * button, with Cmd/Ctrl+Enter to submit and a refocus after sending. The actual
 * delivery is delegated to `onSend` so each surface (the inbox thread, the trip
 * card) can post to its own endpoint; `onSent` fires afterwards (e.g. to pull the
 * sent message and scroll). `extraActions` renders extra buttons inline (e.g. the
 * trip page's "Use Template"). `replyTo` quotes the message being replied to
 * above the reply box; the caller sends it along and clears it.
 */
export function WhatsAppMessageComposer({
	onSend,
	onSent,
	sealed,
	outsideServiceWindow,
	disabled,
	placeholder = "Type your WhatsApp reply or caption. Drop or paste a file to attach it.",
	extraActions,
	typingIndicator,
	onType,
	onStopTyping,
	replyTo,
	onCancelReply,
}: {
	onSend: (payload: {
		message: string
		attachment: File | null
		hasAttachmentField: boolean
	}) => Promise<void>
	onSent?: () => void | Promise<void>
	sealed?: boolean
	// Free text is rejected outside the 24h customer service window. The send
	// stays enabled — the operator may know something we do not, and a failure
	// surfaces on the message either way — but they are told first.
	outsideServiceWindow?: boolean
	disabled?: boolean
	placeholder?: string
	extraActions?: React.ReactNode
	// The "X is typing…" hint, rendered above the reply box.
	typingIndicator?: React.ReactNode
	// Fired as the operator types (throttled by the caller) and when they stop
	// (a successful send or leaving the box), to drive the typing indicator.
	onType?: () => void
	onStopTyping?: () => void
	// The message the reply answers, and how to stop answering it.
	replyTo?: TWhatsAppMessage | null
	onCancelReply?: () => void
}) {
	const [showAttachmentField, toggleAttachmentFieldVisibility] = useState(false)

	// Refocus the reply box after a send so the operator can keep typing.
	const composerRef = useRef<HTMLFormElement>(null)
	const focusComposer = () => {
		composerRef.current?.querySelector<HTMLTextAreaElement>("textarea")?.focus()
	}

	// Cmd+Enter (mac) / Ctrl+Enter (win) submits the reply. @github/hotkey (behind
	// ui-hotkey) only fires a hotkey typed inside a form field when the field
	// carries an id and the fire target a matching `data-hotkey-scope`. The id is
	// unique per instance so multiple composers on one page (the trip page) don't
	// collide.
	const fieldId = `wa-composer-${useId().replace(/:/g, "")}`
	const sendHotkeyRef = useHotkey<HTMLButtonElement>("Mod+Enter")

	// Picking a message to reply to is the start of typing the reply.
	const replyToId = replyTo?.id
	useEffect(() => {
		if (replyToId) {
			composerRef.current
				?.querySelector<HTMLTextAreaElement>("textarea")
				?.focus()
		}
	}, [replyToId])

	if (sealed) {
		return (
			<Text fontSize="sm" color="muted">
				This conversation is sealed because the recipient's WhatsApp identity
				changed. Future messages will create a new conversation.
			</Text>
		)
	}

	return (
		<Form<TComposerValues>
			keepDirtyOnReinitialize={false}
			initialValues={{ custom_message: "", attachment: null }}
			onSubmit={withServerErrors(async (values, form) => {
				const message = values.custom_message?.trim() || ""
				const attachment = values.attachment ?? null
				if (!message && !attachment) {
					throw new Error("Please type a message or attach a file.")
				}
				await onSend({
					message,
					attachment,
					hasAttachmentField: showAttachmentField,
				})
				form.restart()
				// Revert to the plain text form: hide the attachment field so
				// the native file input unmounts and its selected file clears.
				toggleAttachmentFieldVisibility(false)
				// The message is out — clear our typing hint on the other clients.
				onStopTyping?.()
				showSnackbar("WhatsApp message sent successfully.")
				focusComposer()
				await onSent?.()
			})}
			subscription={{ submitting: true }}
		>
			{({ handleSubmit, submitting, form }) => (
				<form
					ref={composerRef}
					noValidate
					onSubmit={handleSubmit}
					// Keystrokes in the textarea bubble here, so a single handler on
					// the form feeds the typing indicator without wrapping the field's
					// own (react-final-form) onChange.
					onInput={onType ? () => onType() : undefined}
					onBlur={
						onStopTyping
							? (e) => {
									// Only stop when focus leaves the composer entirely — not
									// when it hops between the textarea, paperclip and Send.
									if (
										!e.currentTarget.contains(e.relatedTarget as Node | null)
									) {
										onStopTyping()
									}
								}
							: undefined
					}
				>
					{/* The whole reply box takes a dropped or pasted file into the
					    attachment field, the text typed going as its caption. The
					    "Attachment" button stays the way in by keyboard. */}
					<FileDropAreaField
						name="attachment"
						accept={ATTACHMENT_ACCEPT}
						label="Drop a file to attach"
						isDisabled={disabled}
						rejectionMessage={attachmentRejectionMessage}
						oneFileMessage="A WhatsApp message carries one attachment. Add one file."
						onFiles={() => toggleAttachmentFieldVisibility(true)}
					>
						<Stack gap="3">
							{typingIndicator}
							{outsideServiceWindow ? (
								<Alert status="warning" title="Outside the 24 hour window">
									<Text fontSize="sm">
										This customer has not messaged in the last{" "}
										{CUSTOMER_SERVICE_WINDOW_HOURS} hours, so WhatsApp will
										reject a plain message. Send an approved template to reopen
										the conversation.
									</Text>
								</Alert>
							) : null}
							{showAttachmentField ? (
								<Box>
									<FileInputField
										name="attachment"
										accept={ATTACHMENT_ACCEPT}
										help="Or drop a file on the reply box, or paste a screenshot. JPEG/PNG up to 5 MB, MP4 up to 16 MB, documents or audio; the text above is its caption. A WebP goes as a 512×512 sticker."
									/>
								</Box>
							) : null}
							{replyTo ? (
								<Inline gap="2" alignItems="start">
									<Box flex="1" minWidth="0">
										<WhatsAppQuotedMessage quoted={replyTo} />
									</Box>
									<Button
										type="button"
										level="tertiary"
										inline
										size="sm"
										title="Cancel reply"
										onClick={onCancelReply}
									>
										<Icons.Cancel />
									</Button>
								</Inline>
							) : null}
							<TextAreaInputField
								id={fieldId}
								name="custom_message"
								rows={3}
								placeholder={placeholder}
							/>
							<SubmissionError />
							<Inline
								gap="4"
								flexDirection="rowReverse"
								justifyContent="start"
								alignItems="center"
								flexWrap="wrap"
							>
								<Tooltip content="Send (Cmd/Ctrl + Enter)">
									<Box>
										<Button
											ref={sendHotkeyRef}
											data-hotkey-scope={fieldId}
											type="submit"
											disabled={disabled || submitting}
											size="sm"
										>
											{submitting ? "Sending..." : "Send"}{" "}
											<Icons.PaperAirplane rotate="90" />
										</Button>
									</Box>
								</Tooltip>
								<Button
									level="tertiary"
									inline
									title="Add Attachment"
									onClick={() => {
										toggleAttachmentFieldVisibility(!showAttachmentField)
										form.change("attachment", null)
									}}
								>
									<Icons.PaperClip /> Attachment
								</Button>
								{extraActions}
							</Inline>
						</Stack>
					</FileDropAreaField>
				</form>
			)}
		</Form>
	)
}

function attachmentRejectionMessage(file: File): string {
	return `WhatsApp does not send ${file.name} as an attachment. Send a JPEG or PNG image, an MP4 video, audio or a document.`
}
